import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import ModelService from '../../services/ModelService';
import ReviewsService from '../../services/ReviewsService';

export const modelKeys = {
  all: ['models'],
  lists: () => [...modelKeys.all, 'list'],
  list: (filters) => [...modelKeys.lists(), { filters }],
  byCity: (cityId, gender, filters, priceRange) => [
    ...modelKeys.lists(),
    'city',
    cityId,
    gender,
    { filters, priceRange },
  ],
  top: (id) => [...modelKeys.all, 'top', id],
  info: (modelId) => [...modelKeys.all, 'info', modelId],
  abouts: (modelId) => [...modelKeys.all, 'abouts', modelId],
  about: (modelId, section) => [...modelKeys.all, 'about', modelId, section],
  services: (modelId) => [...modelKeys.all, 'services', modelId],
  reviews: (modelId) => [...modelKeys.all, 'reviews', modelId],
  reviewsAll: (modelId, filters = {}) => [
    ...modelKeys.all,
    'reviews-all',
    modelId,
    filters,
  ],
  follower: (modelId, clientId) => [
    ...modelKeys.all,
    'follower',
    modelId,
    clientId,
  ],
};

export function useModelsInfinite(
  cityId,
  gender,
  filters = [],
  priceRange = {}
) {
  return useInfiniteQuery({
    queryKey: modelKeys.byCity(cityId, gender, filters, priceRange),
    queryFn: async ({ page = 1 }) => {
      const models = await ModelService.findByGender({
        gender,
        page,
        cityId,
        filters,
        minPrice: priceRange.min || null,
        maxPrice: priceRange.max || null,
      });

      return {
        models: models || [],
        nextPage: models?.length >= 10 ? page + 1 : undefined,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    enabled: !!cityId && !!gender,
    staleTime: 2 * 60 * 1000, // 2 minutos
  });
}

export function useModelTop(id) {
  return useQuery({
    queryKey: modelKeys.top(id),
    queryFn: () => ModelService.findTopModel(id),
    enabled: !!id,
  });
}

export function useModelInfo(id) {
  return useQuery({
    queryKey: modelKeys.info(id),
    queryFn: () => ModelService.findInfoModel(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000,
    retry: 2,
  });
}

export function useModelAbouts(modelId) {
  return useQuery({
    queryKey: modelKeys.abouts(modelId),
    queryFn: () => ModelService.findAboutsModel(modelId),
    enabled: !!modelId,
    staleTime: 10 * 60 * 1000, // 10 minutos
    gcTime: 30 * 60 * 1000,
    retry: 2,
  });
}

export function useModelServices(modelId) {
  return useQuery({
    queryKey: modelKeys.services(modelId),
    queryFn: () => ModelService.findServicesModel(modelId),
    enabled: !!modelId,
    staleTime: 10 * 60 * 1000, // 10 minutos
    gcTime: 30 * 60 * 1000,
    retry: 2,
  });
}

export function useModelReviews(modelId) {
  return useQuery({
    queryKey: modelKeys.reviews(modelId),
    queryFn: () => ReviewsService.listReviews(modelId),
    enabled: !!modelId,
  });
}

export function useModelAllReviews(modelId, filters = {}) {
  return useQuery({
    queryKey: modelKeys.reviewsAll(modelId, filters),
    queryFn: () => ReviewsService.listAllReviews(modelId, filters),
    enabled: !!modelId,
  });
}

export function useModelFollowerStatus(modelId, clientId) {
  return useQuery({
    queryKey: modelKeys.follower(modelId, clientId),
    queryFn: () => ModelService.followerModel(modelId),
    enabled: !!modelId && !!clientId,
    staleTime: 2 * 60 * 1000, // 2 minutos
    retry: 1,
  });
}

export function useModelFindAboutsSection(modelId, section = 'ABOUT') {
  return useQuery({
    queryKey: modelKeys.about(modelId, section),
    queryFn: () => ModelService.findAboutsSectionModel(modelId, section),
    enabled: !!modelId && !!section,
    staleTime: 2 * 60 * 1000, // 2 minutos
    retry: 1,
  });
}
